import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { notifyEventHost } from './useEventNotifications';
import type { WaiverData } from '@/components/WaiverDialog';

export interface EventParticipant {
  id: string;
  event_id: string;
  user_id: string;
  status: 'invited' | 'going' | 'maybe' | 'declined' | 'waitlist';
  rsvp_at: string | null;
  waiver_signed: boolean;
  waiver_signed_at: string | null;
  waiver_data?: WaiverData;
  notes: string | null;
  created_at: string;
  user_email?: string;
}

export interface EventParticipantWithEmail extends EventParticipant {
  user_email: string;
}

// Fetch participants for an event with user emails
export const useEventParticipants = (eventId: string | undefined) => {
  return useQuery({
    queryKey: ['event-participants', eventId],
    queryFn: async () => {
      if (!eventId) throw new Error('Event ID is required');

      const { data: participants, error } = await supabase
        .from('event_participants')
        .select('*')
        .eq('event_id', eventId);

      if (error) {
        console.error('Error fetching event participants:', error);
        throw error;
      }

      // For now, return participants with user_id as email placeholder
      // In a production app, you'd have a profiles table or use a server function
      const participantsWithEmails: EventParticipantWithEmail[] = (participants || []).map(participant => ({
        ...participant,
        waiver_data: (participant.waiver_data as unknown) as WaiverData | undefined,
        user_email: `user-${participant.user_id.slice(0, 8)}@example.com`,
      }));

      return participantsWithEmails;
    },
    enabled: !!eventId,
  });
};

// RSVP to event
export const useRSVP = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      eventId, 
      status, 
      notes, 
      waiverSigned = false,
      waiverData
    }: { 
      eventId: string; 
      status: EventParticipant['status']; 
      notes?: string;
      waiverSigned?: boolean;
      waiverData?: WaiverData;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User must be authenticated');
      }

      const insertData: any = {
        event_id: eventId,
        user_id: user.id,
        status,
        notes,
      };

      if (waiverSigned) {
        insertData.waiver_signed = true;
        insertData.waiver_signed_at = new Date().toISOString();
        insertData.waiver_data = waiverData || null;
      }

      const { data, error } = await supabase
        .from('event_participants')
        .insert(insertData)
        .select()
        .single();

      if (error) {
        console.error('Error RSVPing to event:', error);
        throw error;
      }

      return { data: (data as unknown) as EventParticipant, eventId, status };
    },
    onSuccess: async ({ eventId, status }, variables) => {
      queryClient.invalidateQueries({ queryKey: ['event-participants', variables.eventId] });
      queryClient.invalidateQueries({ queryKey: ['user-events'] });
      
      // Notify event host
      const statusText = status === 'going' ? 'confirmed their attendance' : 'marked as maybe';
      await notifyEventHost(
        eventId,
        'participant_rsvp',
        'New RSVP',
        `A participant has ${statusText} for your event.`
      );
      
      toast.success('RSVP submitted successfully!');
    },
    onError: (error) => {
      console.error('Failed to RSVP:', error);
      toast.error('Failed to RSVP. Please try again.');
    },
  });
};

// Update RSVP status
export const useUpdateRSVP = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ eventId, status, notes, waiverSigned, waiverData }: { 
      eventId: string; 
      status?: EventParticipant['status']; 
      notes?: string;
      waiverSigned?: boolean;
      waiverData?: WaiverData;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User must be authenticated');
      }

      const updateData: any = {};
      if (status !== undefined) updateData.status = status;
      if (notes !== undefined) updateData.notes = notes;
      if (waiverSigned !== undefined) {
        updateData.waiver_signed = waiverSigned;
        updateData.waiver_signed_at = waiverSigned ? new Date().toISOString() : null;
        updateData.waiver_data = waiverData || null;
      }

      const { data, error } = await supabase
        .from('event_participants')
        .update(updateData)
        .eq('event_id', eventId)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) {
        console.error('Error updating RSVP:', error);
        throw error;
      }

      return { data: data as unknown as EventParticipant, eventId, status };
    },
    onSuccess: async ({ eventId, status }, variables) => {
      queryClient.invalidateQueries({ queryKey: ['event-participants', variables.eventId] });
      queryClient.invalidateQueries({ queryKey: ['user-events'] });
      
      // Notify event host about status change
      const statusText = status === 'going' ? 'confirmed their attendance' : status === 'maybe' ? 'changed to maybe' : 'declined';
      await notifyEventHost(
        eventId,
        'participant_status_change',
        'RSVP Status Changed',
        `A participant has ${statusText}.`
      );
      
      toast.success('RSVP updated successfully!');
    },
    onError: (error) => {
      console.error('Failed to update RSVP:', error);
      toast.error('Failed to update RSVP. Please try again.');
    },
  });
};

// Remove participant (host only)
export const useRemoveParticipant = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ eventId, userId }: { eventId: string; userId: string }) => {
      const { error } = await supabase
        .from('event_participants')
        .delete()
        .eq('event_id', eventId)
        .eq('user_id', userId);

      if (error) {
        console.error('Error removing participant:', error);
        throw error;
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['event-participants', variables.eventId] });
      toast.success('Participant removed successfully');
    },
    onError: (error) => {
      console.error('Failed to remove participant:', error);
      toast.error('Failed to remove participant. Please try again.');
    },
  });
};

// Leave event (participant removes themselves)
export const useLeaveEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (eventId: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User must be authenticated');
      }

      const { error } = await supabase
        .from('event_participants')
        .delete()
        .eq('event_id', eventId)
        .eq('user_id', user.id);

      if (error) {
        console.error('Error leaving event:', error);
        throw error;
      }
    },
    onSuccess: (_, eventId) => {
      queryClient.invalidateQueries({ queryKey: ['event-participants', eventId] });
      queryClient.invalidateQueries({ queryKey: ['user-events'] });
      toast.success('You have left the event');
    },
    onError: (error) => {
      console.error('Failed to leave event:', error);
      toast.error('Failed to leave event. Please try again.');
    },
  });
};
